<template>
  <div class="duration-picker">
    <div v-if="min" class="duration-picker__for-display v-centerer" @click="edit">
      <div v-if="showDays">
        <span class="legend">{{ `${days}` }}</span>
        <span>{{ $tc(`message.DAYS`, days).substring(0, 1) }}</span>
      </div>
      <div>
        <span class="legend">{{ `${hours}` }}</span>
        <span>{{ $tc(`message.HOURS`, hours).substring(0, 1).toLowerCase() }}</span>
      </div>
      <div>
        <span class="legend">{{ `${minutes}` }}</span>
        <span>{{ $tc(`message.MINUTES`, minutes).substring(0, 1).toLowerCase() }}</span>
      </div>
      <div v-if="showSeconds">
        <span class="legend">{{ `${seconds}` }}</span>
        <span>{{ $tc(`message.SECONDS`, seconds).substring(0, 1).toLowerCase() }}</span>
      </div>
    </div>
    <div v-else class="duration-picker__for-display v-centerer" @click="edit">
      <div v-if="showDays">
        <span class="legend">{{ `${days}&nbsp;` }}</span>
        <span>{{ $tc(`message.DAYS`, days) }}</span>
      </div>
      <div>
        <span class="legend">{{ `${hours}&nbsp;` }}</span>
        <span>{{ $tc(`message.HOURS`, hours) }}</span>
      </div>
      <div>
        <span class="legend">{{ `${minutes}&nbsp;` }}</span>
        <span>{{ $tc(`message.MINUTES`, minutes) }}</span>
      </div>
      <div v-if="showSeconds">
        <span class="legend">{{ `${seconds}&nbsp;` }}</span>
        <span>{{ $tc(`message.SECONDS`, seconds) }}</span>
      </div>
    </div>

    <Modal v-model="editing" class="duration-picker__modal">
      <div class="duration-picker__modal-content">
        <div class="duration-picker__modal-content__fields v-centerer">
          <div v-if="showDays" class="duration-picker__modal-content__fields__field">
            <InputNumber v-model="days" />
            <div>{{ $tc(`message.DAYS`, days) }}</div>
          </div>
          <div class="duration-picker__modal-content__fields__field">
            <InputNumber v-model="hours" :min="0" :max="23" />
            <div>{{ $tc(`message.HOURS`, hours) }}</div>
          </div>
          <div class="duration-picker__modal-content__fields__field">
            <InputNumber v-model="minutes" :min="0" :max="59" />
            <div>{{ $tc(`message.MINUTES`, minutes) }}</div>
          </div>
          <div v-if="showSeconds" class="duration-picker__modal-content__fields__field">
            <InputNumber v-model="seconds" :min="0" :max="59" />
            <div>{{ $tc(`message.SECONDS`, seconds) }}</div>
          </div>
        </div>

        <div class="h-v-centerer duration-picker__modal-content__buttons">
          <button class="action-btn" @click="saveAndClose">
            {{ $t('message.DONE') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Component sed to select a time period uing multiple input fields.
 *
 * @vue-prop {Number} value - Duration in seconds
 */

// Components
import InputNumber from '@/components/input-number.vue';
import Modal from '@/components/modal.vue';
// Utils
import { secondsToArray } from '@/utils/main';

export default {

  name: 'DurationPicker',

  components: {
    InputNumber,
    Modal,
  },

  props: {
    value: {
      type: Number,
      required: true,
    },
    showSeconds: {
      type: Boolean,
      default: true,
    },
    showDays: Boolean,
    min: Boolean,
  },

  data() {
    return {
      tmpValue: null,
      editing: false,
    };
  },

  computed: {
    /** Array: duration split into Hours/Minutes/Seconds/days */
    durationArray() {
      return secondsToArray(this.tmpValue);
    },
    /** Number: of days */
    days: {
      get() {
        return Number(this.durationArray[3]);
      },
      set(days) {
        this.tmpValue = days * 86400 + this.hours * 3600 + this.minutes * 60 + this.seconds;
      },
    },
    /** Number: of hours */
    hours: {
      get() {
        return Number(this.durationArray[0]);
      },
      set(hours) {
        this.tmpValue = this.days * 86400 + hours * 3600 + this.minutes * 60 + this.seconds;
      },
    },
    /** Number: of minutes */
    minutes: {
      get() {
        return Number(this.durationArray[1]);
      },
      set(minutes) {
        this.tmpValue = this.days * 86400 + this.hours * 3600 + minutes * 60 + this.seconds;
      },
    },
    /** Number: of seconds */
    seconds: {
      get() {
        return Number(this.durationArray[2]);
      },
      set(seconds) {
        this.tmpValue = this.days * 86400 + this.hours * 3600 + this.minutes * 60 + seconds;
      },
    },
  },

  watch: {
    value() {
      this.tmpValue = this.value;
    },
  },

  created() {
    this.tmpValue = this.value;
  },

  methods: {
    edit() {
      this.editing = true;
    },
    saveAndClose() {
      this.editing = false;
      this.$emit('input', this.tmpValue);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.duration-picker {

  &__for-display {
    padding: 0.2em 0.5em;
    cursor: pointer;

    > div:not(:last-child) {
      margin-right: 0.5em;
    }

    &:hover {
      background: $color-hoverbtn-light-blue;
    }
  }

  &__modal.modal.dark-overlay .dark-overlay-content {
    width: auto;
    height: auto;
  }

  &__modal-content {
    padding: 2em;

    &__fields {

      &__field {
        text-align: center;

        &:not(:last-child) {
          margin-right: 1em;
        }

        input.input-number {
          width: 5em;
          padding: 0.3em 0.5em;
          border-radius: 0.1em;
        }
      }
    }

    &__buttons {
      margin-top: 2.5em;
    }
  }

}
</style>
